import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { fetchMeta, submitFreeEvent, uploadImage } from "../lib/api";
import { COUNTY_LIST } from "../lib/counties";
import { ArrowLeft, ImagePlus, Loader2, X } from "lucide-react";
import AgeRangePicker from "../components/AgeRangePicker";

const BLANK = {
    title: "",
    description: "",
    county: "Fairfax County",
    city: "",
    venue: "",
    start_date: "",
    end_date: "",
    season: "Spring",
    gender: "Boys",
    age_ranges: ["Youth"],
    event_type: "Tournament",
    organizer: "",
    url: "",
    image_url: null,
    submitter_email: "",
};

export default function SubmitPage() {
    const [meta, setMeta] = useState(null);
    const [form, setForm] = useState(BLANK);
    const [submitting, setSubmitting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [searchParams] = useSearchParams();
    const cancelled = searchParams.get("cancelled") === "1";
    const navigate = useNavigate();

    useEffect(() => {
        fetchMeta().then(setMeta);
    }, []);

    const set = (k) => (v) => setForm((p) => ({ ...p, [k]: v }));

    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
    const imagePreviewUrl = form.image_url
        ? `${BACKEND_URL}${form.image_url}`
        : null;

    const handleImagePick = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // reset so same file can be picked again after clear
        if (!file) return;
        if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
            toast.error("Please choose a JPG, PNG, or WebP image");
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Image must be under 5 MB");
            return;
        }
        setUploading(true);
        try {
            const { url } = await uploadImage(file);
            set("image_url")(url);
            toast.success("Image uploaded");
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Upload failed, please retry",
            );
        } finally {
            setUploading(false);
        }
    };

    const clearImage = () => set("image_url")(null);

    const submit = async () => {
        const required = [
            ["title", "Event title"],
            ["county", "County"],
            ["start_date", "Start date"],
            ["url", "Registration URL"],
            ["submitter_email", "Your email"],
        ];
        for (const [k, label] of required) {
            if (!form[k] || !String(form[k]).trim()) {
                toast.error(`${label} is required`);
                return;
            }
        }
        if (!/^https?:\/\//i.test(form.url)) {
            toast.error("Registration URL must start with http:// or https://");
            return;
        }
        if (!/.+@.+\..+/.test(form.submitter_email)) {
            toast.error("Please provide a valid email address");
            return;
        }
        if (!form.age_ranges?.length) {
            toast.error("Pick at least one age range");
            return;
        }

        setSubmitting(true);
        try {
            const { event } = await submitFreeEvent(form);
            navigate("/submit/success", { state: { event } });
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Failed to submit event",
            );
            setSubmitting(false);
        }
    };

    return (
        <div
            className="min-h-screen bg-[#0A0A0A] text-white font-manrope"
            data-testid="submit-page"
        >
            <Toaster theme="dark" position="bottom-right" />

            <header className="sticky top-0 z-30 backdrop-blur-2xl bg-black/70 border-b border-white/10">
                <div className="px-6 md:px-10 py-4 flex items-center justify-between">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-zinc-400 hover:text-white"
                        data-testid="back-home"
                    >
                        <ArrowLeft size={14} /> Back
                    </Link>
                    <div className="font-anton text-xl uppercase tracking-widest">
                        Submit · Event
                    </div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 hidden sm:block">
                        Free · No payment required
                    </div>
                </div>
            </header>

            <main className="px-6 md:px-10 py-10 max-w-3xl mx-auto">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#FF3B30] font-semibold mb-3">
                    For organizers · clubs · camps
                </div>
                <h1 className="font-anton text-5xl sm:text-6xl uppercase leading-[0.95] mb-4">
                    Put your event{" "}
                    <span className="text-[#FF3B30]">on the map</span>.
                </h1>
                <p className="text-sm text-zinc-400 max-w-xl mb-2">
                    List your league, tournament, camp, or clinic on the
                    Virginia map for{" "}
                    <span className="text-white">free</span>. Players click your
                    event card and go straight to your registration page.
                </p>
                <p className="text-xs text-zinc-500 mb-8">
                    Your event appears publicly the moment you submit — no
                    payment, no waiting.
                </p>

                {cancelled && (
                    <div
                        className="mb-6 border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-4 py-3 text-xs uppercase tracking-[0.2em] text-[#FF3B30]"
                        data-testid="cancelled-notice"
                    >
                        Submission cancelled. Try again below.
                    </div>
                )}

                <div className="border border-white/10 bg-[#0d0d0d] p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Event Title *" full>
                        <Input
                            value={form.title}
                            onChange={(e) => set("title")(e.target.value)}
                            placeholder="Fairfax Spring Classic"
                            className="rounded-none bg-black/40 border-white/15"
                            data-testid="s-title"
                        />
                    </Field>
                    <Field label="Description" full>
                        <Textarea
                            value={form.description}
                            onChange={(e) =>
                                set("description")(e.target.value)
                            }
                            rows={3}
                            placeholder="Tell players what to expect…"
                            className="rounded-none bg-black/40 border-white/15"
                            data-testid="s-desc"
                        />
                    </Field>
                    <Field label="County *" full>
                        <SelectRaw
                            value={form.county}
                            onChange={set("county")}
                            options={COUNTY_LIST}
                            testId="s-county"
                        />
                    </Field>
                    <Field label="City">
                        <Input
                            value={form.city}
                            onChange={(e) => set("city")(e.target.value)}
                            className="rounded-none bg-black/40 border-white/15"
                            data-testid="s-city"
                        />
                    </Field>
                    <Field label="Venue">
                        <Input
                            value={form.venue}
                            onChange={(e) => set("venue")(e.target.value)}
                            className="rounded-none bg-black/40 border-white/15"
                            data-testid="s-venue"
                        />
                    </Field>
                    <Field label="Start Date *">
                        <Input
                            type="date"
                            value={form.start_date}
                            onChange={(e) =>
                                set("start_date")(e.target.value)
                            }
                            className="rounded-none bg-black/40 border-white/15"
                            data-testid="s-start"
                        />
                    </Field>
                    <Field label="End Date">
                        <Input
                            type="date"
                            value={form.end_date}
                            onChange={(e) => set("end_date")(e.target.value)}
                            className="rounded-none bg-black/40 border-white/15"
                            data-testid="s-end"
                        />
                    </Field>
                    <Field label="Season">
                        <SelectRaw
                            value={form.season}
                            onChange={set("season")}
                            options={meta?.seasons || []}
                            testId="s-season"
                        />
                    </Field>
                    <Field label="Gender">
                        <SelectRaw
                            value={form.gender}
                            onChange={set("gender")}
                            options={meta?.genders || []}
                            testId="s-gender"
                        />
                    </Field>
                    <Field label="Age Range (pick one or more) *" full>
                        <AgeRangePicker
                            options={meta?.age_ranges || []}
                            value={form.age_ranges}
                            onChange={set("age_ranges")}
                            testIdPrefix="s-age"
                        />
                    </Field>
                    <Field label="Event Type">
                        <SelectRaw
                            value={form.event_type}
                            onChange={set("event_type")}
                            options={meta?.event_types || []}
                            testId="s-type"
                        />
                    </Field>
                    <Field label="Organizer">
                        <Input
                            value={form.organizer}
                            onChange={(e) =>
                                set("organizer")(e.target.value)
                            }
                            placeholder="Your club / org"
                            className="rounded-none bg-black/40 border-white/15"
                            data-testid="s-organizer"
                        />
                    </Field>
                    <Field label="Registration URL *" full>
                        <Input
                            value={form.url}
                            onChange={(e) => set("url")(e.target.value)}
                            placeholder="https://yourclub.com/register"
                            className="rounded-none bg-black/40 border-white/15"
                            data-testid="s-url"
                        />
                        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1.5">
                            Players click your event card and land directly
                            here.
                        </p>
                    </Field>
                    <Field label="Your Email *" full>
                        <Input
                            type="email"
                            value={form.submitter_email}
                            onChange={(e) =>
                                set("submitter_email")(e.target.value)
                            }
                            placeholder="organizer@club.com"
                            className="rounded-none bg-black/40 border-white/15"
                            data-testid="s-email"
                        />
                    </Field>
                    <Field label="Event Image (optional)" full>
                        <div
                            className="border border-dashed border-white/15 bg-black/30 p-4 flex flex-col sm:flex-row gap-4 items-start"
                            data-testid="image-picker"
                        >
                            {imagePreviewUrl ? (
                                <div className="relative w-32 h-32 flex-shrink-0 bg-black border border-white/15 overflow-hidden">
                                    <img
                                        src={imagePreviewUrl}
                                        alt="Event preview"
                                        className="w-full h-full object-cover"
                                        data-testid="image-preview"
                                    />
                                    <button
                                        type="button"
                                        onClick={clearImage}
                                        className="absolute top-1 right-1 bg-black/80 hover:bg-[#FF3B30] p-1 text-white transition-colors"
                                        data-testid="image-clear"
                                        aria-label="Remove image"
                                    >
                                        <X size={12} />
                                    </button>
                                </div>
                            ) : (
                                <div className="w-32 h-32 flex-shrink-0 bg-black/40 border border-white/10 flex items-center justify-center text-zinc-600">
                                    <ImagePlus size={28} />
                                </div>
                            )}
                            <div className="flex-1">
                                <label
                                    htmlFor="event-image-input"
                                    className={[
                                        "inline-flex items-center gap-2 border px-4 py-2 text-xs uppercase tracking-[0.2em] font-semibold cursor-pointer transition-colors",
                                        uploading
                                            ? "border-white/15 text-zinc-500 cursor-wait"
                                            : "border-white/15 hover:border-[#FF3B30] hover:text-[#FF3B30] text-white",
                                    ].join(" ")}
                                    data-testid="image-upload-label"
                                >
                                    {uploading ? (
                                        <>
                                            <Loader2
                                                size={13}
                                                className="animate-spin"
                                            />
                                            Uploading
                                        </>
                                    ) : (
                                        <>
                                            <ImagePlus size={13} />
                                            {imagePreviewUrl
                                                ? "Replace image"
                                                : "Choose image"}
                                        </>
                                    )}
                                </label>
                                <input
                                    id="event-image-input"
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    onChange={handleImagePick}
                                    disabled={uploading}
                                    className="hidden"
                                    data-testid="image-input"
                                />
                                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-2">
                                    JPG · PNG · WebP · max 5 MB
                                </p>
                                <p className="text-[10px] text-zinc-500 mt-1">
                                    Square-ish photos look best (e.g. team
                                    logo, venue shot).
                                </p>
                            </div>
                        </div>
                    </Field>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border border-white/10 bg-[#0d0d0d] p-5">
                    <div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                            Listing
                        </div>
                        <div className="font-anton text-4xl text-white">
                            Free
                        </div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mt-1">
                            Goes live instantly
                        </div>
                    </div>
                    <button
                        onClick={submit}
                        disabled={submitting}
                        className="bg-[#FF3B30] hover:bg-[#FF574D] disabled:opacity-50 disabled:cursor-wait text-white font-anton tracking-widest uppercase text-lg px-8 py-4 transition-colors flex items-center gap-2"
                        data-testid="pay-and-submit-btn"
                    >
                        {submitting ? "Submitting…" : "Submit Event"}
                    </button>
                </div>
            </main>
        </div>
    );
}

const Field = ({ label, children, full }) => (
    <div className={full ? "md:col-span-2" : ""}>
        <Label className="text-[10px] tracking-[0.22em] uppercase text-zinc-500 mb-1.5 block">
            {label}
        </Label>
        {children}
    </div>
);

const SelectRaw = ({ value, onChange, options, testId }) => (
    <Select value={value} onValueChange={onChange}>
        <SelectTrigger
            data-testid={testId}
            className="rounded-none bg-black/40 border-white/15 text-white"
        >
            <SelectValue />
        </SelectTrigger>
        <SelectContent className="rounded-none bg-[#0f0f0f] border-white/15 text-white max-h-[300px]">
            {options.map((o) => (
                <SelectItem
                    key={o}
                    value={o}
                    className="rounded-none focus:bg-[#FF3B30]/20 focus:text-white"
                >
                    {o}
                </SelectItem>
            ))}
        </SelectContent>
    </Select>
);
